export interface ICatalogCurrency {
  code: string;
  name: string;
  symbol: string;
  id?: number;
}

export interface ITenantCurrency {
  id: number;
  currencyCode: string;
  isDefault: boolean;
  isDeleted: boolean;
}

const ISO_4217_CODES =
  'AED AFN ALL AMD ANG AOA ARS AUD AWG AZN BAM BBD BDT BGN BHD BIF BMD BND BOB BRL BSD BTN BWP BYN BZD CAD CDF CHF CLP CNY COP CRC CUP CVE CZK DJF DKK DOP DZD EGP ERN ETB EUR FJD FKP GBP GEL GHS GIP GMD GNF GTQ GYD HKD HNL HTG HUF IDR ILS INR IQD IRR ISK JMD JOD JPY KES KGS KHR KMF KPW KRW KWD KYD KZT LAK LBP LKR LRD LSL LYD MAD MDL MGA MKD MMK MNT MOP MRU MUR MVR MWK MXN MYR MZN NAD NGN NIO NOK NPR NZD OMR PAB PEN PGK PHP PKR PLN PYG QAR RON RSD RUB RWF SAR SBD SCR SDG SEK SGD SHP SLE SOS SRD SSP STN SYP SZL THB TJS TMT TND TOP TRY TTD TWD TZS UAH UGX USD UYU UZS VES VND VUV WST XAF XCD XOF XPF YER ZAR ZMW ZWL'.split(
    ' '
  );

const isIsoCurrencyCode = (value: string): boolean =>
  /^[A-Za-z]{3}$/.test(value);

let nameToIsoCache: Map<string, string> | undefined;

const nameToIsoMap = (): Map<string, string> => {
  if (nameToIsoCache) return nameToIsoCache;
  nameToIsoCache = new Map();
  if (typeof Intl === 'undefined' || !('DisplayNames' in Intl)) {
    return nameToIsoCache;
  }
  const displayNames = new Intl.DisplayNames(['en'], { type: 'currency' });
  for (const code of ISO_4217_CODES) {
    const label = displayNames.of(code);
    if (label) nameToIsoCache.set(label.toLowerCase(), code);
  }
  return nameToIsoCache;
};

export const toCurrencyCode = (name: string, explicitCode?: string): string => {
  if (explicitCode && isIsoCurrencyCode(explicitCode)) {
    return explicitCode.toUpperCase();
  }
  if (isIsoCurrencyCode(name)) return name.toUpperCase();
  return (
    nameToIsoMap().get(name.trim().toLowerCase()) ??
    explicitCode?.toUpperCase() ??
    ''
  );
};

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

const pickString = (
  record: Record<string, unknown>,
  ...keys: string[]
): string => {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return '';
};

const pickId = (record: Record<string, unknown>): number => {
  const raw = record.id ?? record.Id;
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw === 'string' && raw.trim()) {
    const parsed = Number.parseInt(raw, 10);
    if (Number.isFinite(parsed)) return parsed;
  }
  return 0;
};

export const parseCatalogCurrencies = (
  payload: unknown
): ICatalogCurrency[] => {
  const raw = Array.isArray(payload)
    ? payload
    : asRecord(payload)?.items ?? asRecord(payload)?.currencies;
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((item) => {
    const record = asRecord(item);
    if (!record) return [];
    const name = pickString(record, 'name', 'Name');
    const explicitCode = pickString(
      record,
      'currencyCode',
      'CurrencyCode',
      'code',
      'Code'
    );
    const code = toCurrencyCode(name || explicitCode, explicitCode);
    if (!isIsoCurrencyCode(code)) return [];
    const id = pickId(record);
    return [
      {
        code,
        name: name || code,
        symbol: pickString(record, 'symbol', 'Symbol'),
        ...(id ? { id } : {}),
      },
    ];
  });
};

export const parseTenantCurrencies = (payload: unknown): ITenantCurrency[] => {
  if (!Array.isArray(payload)) return [];
  return payload.flatMap((item) => {
    const record = asRecord(item);
    if (!record) return [];
    const currencyCode = toCurrencyCode(
      pickString(record, 'name', 'Name'),
      pickString(record, 'currencyCode', 'CurrencyCode', 'code', 'Code')
    );
    if (!isIsoCurrencyCode(currencyCode)) return [];
    return [
      {
        id: pickId(record),
        currencyCode,
        isDefault: Boolean(record.isDefault ?? record.IsDefault),
        isDeleted:
          record.isDeleted === true || record.IsDeleted === true ? true : false,
      },
    ];
  });
};

export const toTenantCurrencyPayload = (
  selectedCodes: string[],
  defaultCode: string,
  previous: ITenantCurrency[],
  catalog: ICatalogCurrency[]
): ITenantCurrency[] => {
  const previousByCode = new Map(
    previous.map((item) => [item.currencyCode, item])
  );
  const catalogByCode = new Map(catalog.map((item) => [item.code, item]));
  const codes = [
    ...new Set(
      selectedCodes
        .map((code) => catalogByCode.get(code)?.code ?? toCurrencyCode(code))
        .filter(isIsoCurrencyCode)
    ),
  ];
  return codes.map((code) => {
    const existing = previousByCode.get(code);
    const catalogItem = catalogByCode.get(code);
    return {
      id: existing?.id ?? catalogItem?.id ?? 0,
      currencyCode: code,
      isDefault: code === defaultCode,
      isDeleted: false,
    };
  });
};
